import clsx from "clsx";
import Image from "next/image";

import { Plus, Minus } from "@chef/icons/small";
import { BASE_64_1X1_GREY, language } from "@chef/constants";
import {
  ProductsByCategoriesQuery,
  setStandaloneProductModal,
  setSubscribedStandaloneModal,
} from "@chef/state-management";
import {
  getPriceOfProductByVariation,
  getDiscountedAmount,
  getAttribute,
  getProductFeaturedImage,
  getStockData,
} from "@chef/state-management/helpers";
import { isEqualStrings } from "@chef/utils/equal";
import {
  useAddonsAndGroceriesBasket,
  useAppDispatch,
} from "@chef/state-management/hooks";
import {
  Card,
  ButtonCircle,
  Switch,
  CurrencyWithDiscount,
} from "@chef/components";

import { intl } from "./StandaloneProductTile.Intl";

type StandaloneProductTileProps = {
  product: ProductsByCategoriesQuery["productsByCategories"][number]["products"][number]["variations"][number];
  productTypeId: ProductsByCategoriesQuery["productsByCategories"][number]["products"][number]["productTypeId"];
  showDiscount?: boolean;
  isPrioritisedProduct?: boolean;
} & (
  | {
      type: "subscription";
      week?: never;
      year?: never;
    }
  | {
      type: "week";
      week: number;
      year: number;
    }
);

export const StandaloneProductTile = ({
  type,
  week,
  year,
  product,
  productTypeId,
  showDiscount = true,
  isPrioritisedProduct = false,
}: StandaloneProductTileProps) => {
  const dispatch = useAppDispatch();
  const useAddonsAndGroceriesBasketProps =
    type === "subscription" ? { type } : { type, week, year };
  const {
    basket,
    addOrRemoveProduct,
    subscribedProducts,
    isBillingLoading,
    addProductToSubscription,
  } = useAddonsAndGroceriesBasket(useAddonsAndGroceriesBasketProps);
  const { name, variationId } = product;
  const productId = product.product.productId;

  const variationCountInBasket =
    basket?.find(
      (b) =>
        isEqualStrings(b.productId, productId) &&
        isEqualStrings(b.variationId, variationId),
    )?.quantity ?? 0;

  const { maxPurchasableAmount } = getStockData({
    variation: product,
    addedQuantity: variationCountInBasket,
  });

  const subscribedQuantity =
    subscribedProducts?.find((p) => p.variation.productId === productId)
      ?.quantity ?? 0;

  const imageUrl = getProductFeaturedImage(product);
  const isAllowedToSubscribe = isEqualStrings(
    getAttribute("isAllowedToSubscribe", product),
    "true",
  );

  const price = getPriceOfProductByVariation(product);
  const discountedAmount = getDiscountedAmount(product);
  const isOnSale = !!discountedAmount;

  const onTriggerProductModal = () =>
    dispatch(
      setStandaloneProductModal({
        open: true,
        productByCategoryVariation: product,
      }),
    );

  const onChangeSubscribed = () =>
    dispatch(
      setSubscribedStandaloneModal({
        open: true,
        deviationProduct: {
          productId,
          productTypeId,
          quantity: subscribedQuantity || 0,
          variationId,
          price,
        },
        productName: name,
        maxQtyAllowed: maxPurchasableAmount,
      }),
    );

  const onDecrementItem = () => {
    addOrRemoveProduct({
      productId,
      productTypeId,
      quantity: variationCountInBasket - 1,
      variationId,
      price,
    });
  };

  const onIncrementItem = () => {
    addOrRemoveProduct({
      productId,
      productTypeId,
      quantity: variationCountInBasket + 1,
      variationId,
      price,
    });
  };

  const onAddToSubscription = () => {
    if (addProductToSubscription) {
      addProductToSubscription({
        productId,
        productTypeId,
        quantity: subscribedQuantity ? 0 : variationCountInBasket,
        variationId,
        price,
      });
    }
  };

  const id = `${productId}:${variationId}`;
  const discount = showDiscount ? discountedAmount : 0;
  const isSubscribedTo = !!subscribedQuantity;
  const isShowingAddToSubscription =
    !!variationCountInBasket &&
    isAllowedToSubscribe &&
    !!addProductToSubscription;

  const hasFooter =
    !!addProductToSubscription &&
    !!isAllowedToSubscribe &&
    !!variationCountInBasket;

  return (
    <div className="relative flex flex-col h-full">
      <Card
        className={clsx(
          "relative h-full",
          isShowingAddToSubscription && "rounded-b-none",
        )}
        noPadding
      >
        <div className="relative flex h-full md:flex-col">
          <button
            type="button"
            onClick={onTriggerProductModal}
            className="relative w-2/5 aspect-square md:aspect-2/3 md:w-full"
          >
            {isOnSale && (
              <div className="absolute z-10 top-2">
                <p className="p-1 text-xs leading-none rounded-r-2xl -left-2 bg-highlight">
                  {intl.CAMPAIGN}
                </p>
              </div>
            )}
            <Image
              alt={name}
              src={imageUrl || BASE_64_1X1_GREY}
              fill
              className={clsx(
                "object-cover md:object-fill rounded-tl md:rounded-none md:rounded-t",
                !hasFooter && "rounded-bl",
              )}
              priority={isPrioritisedProduct}
              sizes="(min-width: 768px) 25vw, 20vw"
            />
          </button>

          <div
            className="flex flex-col justify-between flex-grow w-full h-full p-2 md:p-3"
            id={id}
          >
            <button
              type="button"
              onClick={onTriggerProductModal}
              className="text-left line-clamp-2"
            >
              {name}
            </button>

            <div className="flex items-end justify-between w-full mt-2">
              <div>
                <CurrencyWithDiscount
                  language={language}
                  className="mt-2"
                  discount={discount}
                  total={price}
                  emphasized
                />
              </div>

              <div className="flex items-center">
                {!!variationCountInBasket && (
                  <>
                    <ButtonCircle
                      small
                      onClick={onDecrementItem}
                      Icon={Minus}
                      id={`${id}-minus-btn`}
                    />
                    <span className="mx-2">{variationCountInBasket}</span>
                  </>
                )}
                <ButtonCircle
                  small
                  outlined={!!variationCountInBasket}
                  primary
                  onClick={onIncrementItem}
                  Icon={Plus}
                  id={`${id}-add-btn`}
                  disabled={variationCountInBasket >= maxPurchasableAmount}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
      {isShowingAddToSubscription && (
        <div
          id="add-to-subscription"
          className={clsx(
            "relative md:absolute md:bottom-0 md:translate-y-full flex px-2 py-3 text-sm align-middle rounded-b w-full shadow-lg",
            isSubscribedTo ? "bg-informationBG" : "bg-background",
          )}
        >
          <Switch
            className="shrink-0"
            small
            checked={isSubscribedTo}
            onChange={onAddToSubscription}
            isLoading={isBillingLoading}
          />

          <span className="pl-2 text-sm">
            {isSubscribedTo ? (
              <>
                <span>
                  {intl.YOU_ARE_SUBSCRIBING_TO} {subscribedQuantity}{" "}
                  {intl.PIECES}{" "}
                </span>
                <button onClick={onChangeSubscribed}>
                  <strong className="underline">{intl.CHANGE}</strong>
                </button>
              </>
            ) : (
              <span>{intl.ADD_TO_YOUR_SUBSCRIPTION}</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
};
